import { useEffect } from 'react';

import {
  useIsFlagCohortResolved,
  useIsSharedPatientDirectoryEnabled,
} from '@/features/flags';
import { setPatientDirectoryGate } from '@/lib/analytics/patient-shared-event-props';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { boundedErrorName } from '@/utils/logger/bounded-error-name';
import { logger } from '@/utils/logger/logger';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';
import { resolveDirectoryGate } from '@heidi/patients/contracts';
import {
  useQuerySharedPatientDirectorySetting,
  useWorkspaceId,
} from '@heidi/workspace/data';

const WORKSPACE_GROUP_TYPE = 'workspace';

/**
 * Sets the Amplitude `workspace` group and its `share_patients_enabled`
 * property, mirroring the `team` group in `src/lib/analytics/scribe/auth.ts`.
 * Never throws, and logs a bounded classification instead of the error object.
 */
function syncWorkspaceCohort(workspaceId: string, shareEnabled: boolean): void {
  try {
    amplitude.setGroup(WORKSPACE_GROUP_TYPE, workspaceId);
    const identify = new AmplitudeIdentify();
    identify.set('share_patients_enabled', shareEnabled);
    amplitude.groupIdentify(WORKSPACE_GROUP_TYPE, workspaceId, identify);
  } catch (error) {
    logger.error('[Amplitude] Failed to set workspace group', {
      error_name: boundedErrorName(error),
    });
  }
}

/**
 * Keeps the two emit-time inputs for the patient shared-directory analytics
 * current (COLAB-422):
 *
 * 1. the directory gate snapshot the patient event hosts read synchronously;
 * 2. the Amplitude workspace cohort, written once the gate resolves.
 *
 * Resolves the gate with the same tri-state rule as the permission context:
 * flag off is `false`, flag on with the setting pending is unresolved. The gate
 * is also unresolved until the flag provider has resolved. An unresolved gate
 * writes nothing, so a loading flag or setting is never reported as "directory
 * off".
 *
 * Mount once inside the authenticated tree. Renders nothing.
 */
export function usePatientDirectoryAnalyticsSync(): void {
  const isFlagEnabled = useIsSharedPatientDirectoryEnabled();
  // The flag hook returns its default (`false`) until the provider resolves, so
  // an unresolved provider is indistinguishable from "directory off". Analytics
  // needs the tri-state: report nothing until the flag reflects the user's real
  // assignment, or early events and the cohort would record a transient `false`.
  const isFlagResolved = useIsFlagCohortResolved();
  const workspaceId = useWorkspaceId();
  const { data: settingEnabled, isPending } = useQuerySharedPatientDirectorySetting({
    enabled: isFlagEnabled,
  });
  const gate = isFlagResolved
    ? resolveDirectoryGate(isFlagEnabled, settingEnabled, isPending)
    : undefined;

  useEffect(() => {
    setPatientDirectoryGate(gate);
    // A stale gate must not outlive the tree that resolved it (logout).
    return () => setPatientDirectoryGate(undefined);
  }, [gate]);

  useEffect(() => {
    if (!isSaasTelemetryEnabled || !workspaceId || gate === undefined) return;
    syncWorkspaceCohort(workspaceId, gate);
  }, [workspaceId, gate]);
}
